import React, { useEffect } from 'react';
import { DatePicker, Form, Modal, Select, Spin } from 'antd';
import type { Dayjs } from 'dayjs';
import { API_DATE_FORMAT, DISPLAY_DATE_FORMAT } from './utils/dateRange';
import { InlineNotice } from './Styles';
import { useCreatorOptions } from './hooks/useSelectOptions';
import { useDraftPayout } from './hooks/useCreatorMutations';
import type { CreatePayoutPayload } from './types';

const { RangePicker } = DatePicker;

interface DraftFormValues {
  creatorId: string;
  period: [Dayjs, Dayjs];
}

interface DraftPayoutModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * §5.1 — draft a payout.
 *
 * The amount is never entered: it's derived server-side from the rows swept up, so
 * this form only picks who and which period.
 *
 * Worth knowing while reading this: `periodStart` is bookkeeping/display only. The
 * sweep takes every payable row created before `periodEnd` regardless of how far back
 * it goes, so the copy below says so rather than implying the range bounds the sweep.
 */
const DraftPayoutModal: React.FC<DraftPayoutModalProps> = ({ open, onClose }) => {
  const [form] = Form.useForm<DraftFormValues>();
  const { options: creatorOptions, loading: creatorsLoading } = useCreatorOptions();
  const { mutate: draftPayout, isPending } = useDraftPayout();

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const handleSubmit = (values: DraftFormValues) => {
    const [periodStart, periodEnd] = values.period;

    const payload: CreatePayoutPayload = {
      creatorId: values.creatorId,
      periodStart: periodStart.format(API_DATE_FORMAT),
      periodEnd: periodEnd.format(API_DATE_FORMAT),
    };

    draftPayout(payload, { onSuccess: onClose });
  };

  return (
    <Modal
      title="Draft Payout"
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Draft Payout"
      confirmLoading={isPending}
      destroyOnHidden
      width={520}
    >
      <InlineNotice>
        Drafting sweeps every payable row for this creator created before the period
        end — including rows older than the period start, which is recorded for
        reference only. The amount is calculated from those rows and cannot be edited.
      </InlineNotice>

      <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={isPending}>
        <Form.Item
          label="Creator"
          name="creatorId"
          rules={[{ required: true, message: 'Please select a creator' }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Select a creator"
            loading={creatorsLoading}
            options={creatorOptions}
            notFoundContent={creatorsLoading ? <Spin size="small" /> : 'No creators yet'}
          />
        </Form.Item>

        <Form.Item
          label="Period"
          name="period"
          extra="The end date must be after the start date."
          rules={[
            { required: true, message: 'Please select a period' },
            {
              validator: (_, value: [Dayjs, Dayjs] | undefined) => {
                if (!value?.[0] || !value?.[1]) return Promise.resolve();
                return value[1].isAfter(value[0])
                  ? Promise.resolve()
                  : Promise.reject(new Error('End date must be after the start date'));
              },
            },
          ]}
        >
          <RangePicker style={{ width: '100%' }} format={DISPLAY_DATE_FORMAT} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default DraftPayoutModal;
